import React from "react";
import { RMFlightWrapper, Mapper, RMLocation } from "../../../types";
import { changeSelectedFlight } from "../searchFlightsSlice";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { trackFlightDetails } from "../../analytics/AnalyticsActions";
import { selectAirlineLinks } from "../../config/ConfigSlice";
import CodeShareIcon from "./CodeShareIcon";
import LowCostIcon from "./LowCostIcon";
import SearchFlightsResultRange from "./SearchFlightsResultRange";
import { useTranslation } from "react-i18next";
import AirlineLogo from "./AirlineLogo";
import { getLocalizedRouteLabel } from "../../../utils";

type SearchFlightsResultANAProps = {
  result: RMFlightWrapper;
  dateMin: Date;
  dateMax: Date;
  mapper: Mapper;
  originLocation?: RMLocation;
  destinationLocation?: RMLocation;
};

function SearchFlightsResultANA({
  result,
  dateMin,
  dateMax,
  mapper,
  originLocation,
  destinationLocation,
}: SearchFlightsResultANAProps) {
  const dispatch = useAppDispatch();
  const airlineLinks = useAppSelector(selectAirlineLinks);
  const { t } = useTranslation();

  const urlParams = new URLSearchParams(window.location.search);
  const locale = urlParams.get("lng");

  // Generate localized route label for Japanese locale
  const routeLabel =
    locale === "ja" && originLocation && destinationLocation
      ? getLocalizedRouteLabel(
          originLocation,
          destinationLocation,
          result.connxLeg1ArrivalName,
          result.connxLeg1ArrivalPortName,
          result.connxLeg2ArrivalName,
          result.connxLeg2ArrivalPortName
        )
      : result.routeKey;

  const hours = Math.floor(result.totalTravelTime / 60);
  const minutes = result.totalTravelTime % 60;
  const formattedDuration =
    locale === "ja" ? `${hours}時間 ${minutes}分` : `${hours}h ${minutes}m`;

  const selectFlight = () => {
    dispatch(changeSelectedFlight(result));
    dispatch(trackFlightDetails(result));
  };

  const generateAirlineLabel = (airlineLabel, airlineCode) => {
    if (locale === "ja") {
      return "";
    }

    const airlineLink = mapper.includeAirlineLinks
      ? airlineLinks.find((airline) => airline.code === airlineCode)
      : null;

    if (airlineLink) {
      return (
        <a
          href={`//${airlineLink.url}`}
          target="_blank"
          title={airlineLink.url}
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {airlineLabel}
        </a>
      );
    } else {
      return airlineLabel;
    }
  };

  return (
    <tr
      onClick={selectFlight}
      title={t("TITLE_CLICK_FOR_DETAILS")}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          selectFlight();
        }
      }}
    >
      <td>
        <div className="columns is-vcentered">
          <div className="column is-hidden-mobile is-narrow">
            {(result.connxLeg2OperatedBy === null ||
              result.connxLeg2OperatedBy === result.operatedBy) && (
              <AirlineLogo airlineCode={result.operatedBy} />
            )}
            {result.connxLeg2OperatedBy !== null &&
              result.connxLeg2OperatedBy !== result.operatedBy && (
                <AirlineLogo airlineCode="shared-flight-indicator" />
              )}
          </div>
          <div className="column">
            <div className="content">
              <div className="is-size-7">
                {generateAirlineLabel(result.airline, result.operatedBy)}{" "}
                {`${result.operatedBy}${result.flightNo}`}
                {(result.codeshareInd || result.sadName) && <CodeShareIcon />}
                {result.connxLeg2OperatedBy != null &&
                  result.connxLeg2Airline !== result.airline && (
                    <>
                      <span> / </span>
                      {generateAirlineLabel(
                        result.connxLeg2Airline,
                        result.connxLeg2OperatedBy
                      )}
                      {` ${result.connxLeg2OperatedBy}${result.connxLeg2FlightNo}`}
                      {(result.connxLeg2CodeshareInd ||
                        result.connxLeg2SadName) && <CodeShareIcon />}
                      {mapper.enableLowCostIndicator &&
                        (result.lowCost || result.connxLeg2LowCost) && (
                          <LowCostIcon />
                        )}
                    </>
                  )}
                {result.connxLeg2OperatedBy != null &&
                  result.connxLeg2Airline === result.airline && (
                    <>
                      {` / ${result.connxLeg2OperatedBy}${result.connxLeg2FlightNo}`}
                    </>
                  )}
                {result.connxLeg3OperatedBy != null &&
                  result.connxLeg3Airline !== result.connxLeg2Airline && (
                    <>
                      <span> / </span>
                      {generateAirlineLabel(
                        result.connxLeg3Airline,
                        result.connxLeg3OperatedBy
                      )}
                      {` ${result.connxLeg3OperatedBy}${result.connxLeg3FlightNo}`}
                      {(result.connxLeg3CodeshareInd ||
                        result.connxLeg3SadName) && <CodeShareIcon />}
                      {mapper.enableLowCostIndicator &&
                        (result.lowCost || result.connxLeg3LowCost) && (
                          <LowCostIcon />
                        )}
                    </>
                  )}
                {result.connxLeg3OperatedBy != null &&
                  result.connxLeg3Airline === result.connxLeg2Airline && (
                    <>
                      {` / ${result.connxLeg3OperatedBy}${result.connxLeg3FlightNo}`}
                    </>
                  )}
              </div>
              <div className="is-size-7">
                {`${routeLabel} ${
                  result.connxLeg2FlightNo === null
                    ? result.stops === "0"
                      ? t("LABEL_FLIGHT_FINDER_NONSTOP")
                      : result.stops +
                        " " +
                        t("LABEL_FLIGHT_FINDER_NUMBER_STOPS")
                    : ""
                }`}
              </div>
              <div className="summary-row-departure-time is-size-7">{`${
                result.departureTimeAsString
              } - ${result.arrivalTimeAsString} ${
                result.arrivalDayOffset > 0
                  ? `(+${result.arrivalDayOffset})`
                  : ""
              } (${formattedDuration})`}</div>
            </div>
          </div>
        </div>
        <SearchFlightsResultRange
          frequency={result.frequency}
          effectiveFrom={result.effectiveFrom}
          effectiveTo={result.effectiveTo}
          dateMin={dateMin}
          dateMax={dateMax}
          density="normal"
        />
      </td>
    </tr>
  );
}

export default SearchFlightsResultANA;
